import React, { useState, useEffect, useContext, useRef } from "react";
import Field from "./Field";
import styles from "./Login.module.css";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import jwtDecode from "jwt-decode";
import { useDispatch } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { signup, signin } from "../../actions/auth";
import {
  Button,
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
} from "@material-ui/core";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import EventBusyOutlinedIcon from "@material-ui/icons/EventBusyOutlined";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import AccessExpiredDialog from "./AccessExpiredDialog";
import { createProfile } from "../../actions/profile";
import { useSnackbar } from "../../utils/useSnackbar";
import * as api from "../../api";
import CircularProgress from "@material-ui/core/CircularProgress";
import { StockContext } from "../../Context/StockContext";
import { usePermissionsContext } from "../../Context/PermissionsContext";

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  profilePicture: "",
  bio: "",
};
const Login = () => {
  const [formData, setFormData] = useState(initialState);
  const [isSignup, setIsSignup] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  // eslint-disable-next-line
  const [openSnackbar, closeSnackbar] = useSnackbar();
  const user = JSON.parse(localStorage.getItem("profile"));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

    // Reminder modal
    const [showReminder, setShowReminder] = useState(false);
    const [reminderData, setReminderData] = useState(null);

    // Persistent pre-login banner - unlike the reminder modal above (which
    // only fires after a successful sign-in, since it needs the specific
    // account), this reads GET /subscription, which is already public/
    // unauthenticated, so it can show before anyone types credentials.
    const [subBanner, setSubBanner] = useState(null);
    useEffect(() => {
      // Guards against "state update on unmounted component" - a login that
      // resolves quickly (or React StrictMode's dev-only double-invoke) can
      // otherwise still be in flight after Login unmounts on redirect. Same
      // cancelled-flag pattern UsersTab.js's viewStoreInfo fetch already uses.
      let cancelled = false;
      (async () => {
        try {
          const { data } = await api.getSubscription();
          if (cancelled) return;
          const sub = data?.data;
          if (!sub?.subscriptionExpiry) return;
          const expiryDate = new Date(sub.subscriptionExpiry);
          const dateLabel = expiryDate.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
          const daysLeft = Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          // Same 10-day urgency window SuperAdminNavbar.js's own chip uses -
          // stays quiet otherwise, no clutter on every single login.
          if (daysLeft < 0) {
            setSubBanner({ tone: "danger", label: `Subscription expired ${Math.abs(daysLeft)}d ago`, dateLabel });
          } else if (daysLeft <= 10) {
            setSubBanner({ tone: "warning", label: `Subscription expires in ${daysLeft}d`, dateLabel });
          }
        } catch (error) {
          // Non-fatal - login just renders without the banner.
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []);

    // Expired modal
    const [showExpired, setShowExpired] = useState(false);
    const [expiredData, setExpiredData] = useState(null);

  // Login has no session yet by definition, so branding comes from the
  // public, unauthenticated company endpoint rather than a generic
  // vendor logo - whoever's POS this is sees their own store name and logo.
  const [publicBrand, setPublicBrand] = useState({
    businessName: null,
    logo: null,
    contactAddress: null,
    supportCallNumber: null,
    supportWhatsappNumber: null,
  });
  useEffect(() => {
    let cancelled = false;
    api
      .fetchPublicCompany()
      .then(({ data }) => {
        if (cancelled) return;
        setPublicBrand({
          businessName: data?.businessName || null,
          logo: data?.logo || null,
          contactAddress: data?.contactAddress || null,
          supportCallNumber: data?.supportCallNumber || null,
          supportWhatsappNumber: data?.supportWhatsappNumber || null,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // StockProvider and PermissionsProvider both mount once at the very app
  // root (see providers.js) and only check "is anyone logged in" the one
  // time they mount - which happens before login, while still on this
  // screen. A client-side navigation (history.push, no reload) wouldn't
  // remount them, so without calling these explicitly right here, unit
  // mappings and permissions would sit empty until PermissionsProvider's
  // own 5-minute poll eventually catches up - buttons would look
  // permission-locked and stock conversions would look broken for however
  // long that takes.
  const { refreshMappings } = useContext(StockContext);
  const permissionsContext = usePermissionsContext();

  // Tracks whether Login is still mounted, for handleSubmit's finally block
  // below - a successful login dispatches AUTH synchronously (see actions/
  // auth.js's signin), which can flip App.js's isSuperAdmin branch and
  // unmount Login before handleSubmit's own async function finishes running
  // (the JS closure keeps executing regardless of React unmounting the
  // component it belongs to). Calling setLoading(false) after that point hit
  // React's "state update on an unmounted component" warning.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const redirectWithWelcome = (url, name) => {
    refreshMappings?.();
    permissionsContext?.refreshPermissions?.();
    openSnackbar(name ? `Welcome back, ${name}!` : "Welcome back!", "success");
    history.push(url);
  };

  const handleShowPassword = () => setShowPassword(!showPassword);
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors((prevErrors) => ({ ...prevErrors, [e.target.name]: '' }));

  };
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
      isValid = false;
    }

    if (!formData.password.trim()) {
      errors.password = "Password is required.";
      isValid = false;
    }

    if (isSignup && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };
  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (!validateForm()) {
  //     //(validateForm())
  //     return;
  //   }
  //   if (isSignup) {
  //     dispatch(signup(formData, openSnackbar, setLoading));
  //   } else {
  //     dispatch(signin(formData, openSnackbar, setLoading));
  //   }
  //   setLoading(true);
  // };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      if (isSignup) {
        const res = await dispatch(signup(formData, setLoading));
        if (res?.token) {
          redirectWithWelcome("/PosMainScreen", res?.result?.name?.split(" ")[0]);
        }
        return;
      }

      const res = await dispatch(signin(formData, openSnackbar, setLoading));
      console.log("res", res)

      if (res?.expired) {
        // ⛔ Show expired dialog
        setExpiredData(res);
        setShowExpired(true);
        return;
      }

      // ⚠ Show reminder dialog ONLY for NORMAL USERS (not super admin)
      if (res?.reminder?.reminder && !res?.result?.userType?.includes("superAdmin")) {
        setReminderData(res.reminder);
        setShowReminder(true);
        return;
      }


      if (res?.token) {
        // ✅ Set invoicePrintCount at login (default SINGLE)
        const p = JSON.parse(localStorage.getItem("profile") || "{}");
        const cnt =
          p?.userProfile?.invoicePrintCount ||
          p?.result?.userProfile?.invoicePrintCount ||
          "SINGLE";
        localStorage.setItem("invoicePrintCount", cnt);

        const firstName = res?.result?.name?.split(" ")[0];
        if (res?.result?.userType?.includes("superAdmin")) {
          redirectWithWelcome("/Subscribers", firstName);
        } else {
          redirectWithWelcome("/PosMainScreen", firstName);
        }
      }

    } catch (err) {
      openSnackbar(err?.message || "Something went wrong", "error");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignup((prevState) => !prevState);
  };

  // const googleSuccess = async (res) => {
  //   const result = jwtDecode(res.credential);
  //   const token = res?.credential;
  //   dispatch(
  //     createProfile({
  //       name: result?.name,
  //       email: result?.email,
  //       userId: result?.jti,
  //       phoneNumber: "",
  //       businessName: "",
  //       contactAddress: "",
  //       logo: result?.picture,
  //       website: "",
  //     })
  //   );

  //   try {
  //     dispatch({ type: "AUTH", data: { result, token } });

  //     window.location.href = "/dashboard";
  //   } catch (error) {
  //     //(error);
  //   }
  // };
  const googleError = (error) => {
    //(error);
    //("Google Sign In was unseccassful. Try again later");
  };

  // if (user) {
  //   history.push("/PosMainScreen");
  // }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.card} style={{ maxWidth: 400 }}>
        {subBanner && (
          <div className={`${styles.subBanner} ${subBanner.tone === "danger" ? styles.subBannerDanger : styles.subBannerWarning}`}>
            {subBanner.tone === "danger" ? (
              <ErrorOutlineOutlinedIcon className={styles.subBannerIcon} />
            ) : (
              <ScheduleOutlinedIcon className={styles.subBannerIcon} />
            )}
            <span className={styles.subBannerText}>
              <span className={styles.subBannerLabel}>{subBanner.label}</span>
              <span className={styles.subBannerDate}>Renew before {subBanner.dateLabel}</span>
            </span>
          </div>
        )}
        <div className={styles.brandBanner} />
        <div className={styles.header2}>
          <div className={styles.logoWrapper} onClick={() => history.push("/")}>
            <img
              className={styles.logoImg}
              src={
                publicBrand.logo ||
                "https://mindtree.co.th/wp-content/uploads/mindtreetechnologies.svg"
              }
              alt={publicBrand.businessName || "Store"}
            />
          </div>
          <Typography className={styles.welcomeTitle}>
            {isSignup
              ? "Create your account"
              : publicBrand.businessName
              ? `Welcome to ${publicBrand.businessName}`
              : "Welcome back"}
          </Typography>
          <Typography className={styles.welcomeSubtitle}>
            {isSignup
              ? "Sign up to get started"
              : "Sign in to access your POS"}
          </Typography>
        </div>
        <div className={styles.formBody}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {isSignup && (
              <>
                <Field
                  name="firstName"
                  label="First Name"
                  handleChange={handleChange}
                  autoFocus
                  half
                />
                <Field
                  name="lastName"
                  label="Last Name"
                  handleChange={handleChange}
                  half
                />
              </>
            )}

            <Field
              name="email"
              label="User Name"
              handleChange={handleChange}
              type="text"
              error={errors.email}
            />
            <Field
              name="password"
              label="Password"
              handleChange={handleChange}
              type={showPassword ? "text" : "password"}
              handleShowPassword={handleShowPassword}
              error={errors.password}
            />
            {isSignup && (
              <Field
                name="confirmPassword"
                label="Repeat Password"
                handleChange={handleChange}
                type="password"
                error={errors.confirmPassword}
              />
            )}
          </Grid>
          <div className={styles.buttons}>
            <div>
              {/* <button className={styles.submitBtn}> { isSignup ? 'Sign Up' : 'Sign In' }</button> */}
              {/* <ProgressButton>{ isSignup ? 'Sign Up' : 'Sign In' }</ProgressButton> */}
              {loading ? (
                <div className={styles.loginBtnLoading}>
                  <CircularProgress size={20} className={styles.spinner} />
                  {isSignup ? "Signing Up…" : "Signing In…"}
                </div>
              ) : (
                <button className={styles.loginBtn}>
                  {isSignup ? "Sign Up" : "Login"}
                </button>
              )}
            </div>
            {/* <div className={styles.option}>
                  <span>or</span>
                </div>
                <div>
                  <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                    <GoogleLogin
                      onSuccess={googleSuccess}
                      onError={googleError}
                      text='continue_with'
                      useOneTap
                      auto_select
                      state_cookie_domain='single_host_origin'
                    />
                  </GoogleOAuthProvider>
                </div> */}
          </div>
          <Grid container alignItems="center">
            {/* <Grid item>
              <Typography>
                {isSignup
                  ? "Already have an account?"
                  : "Don't have an account? "}
                   <Typography className={styles.switchBtn} onClick={switchMode}>
                {isSignup ? " Login" : "Signup"}
              </Typography>
              </Typography>
            </Grid> */}

          </Grid>
  {/* hide this forgot password functionality workon next ver.. */}
          {/* <Link to="forgot">
            <p
              style={{
                textAlign: "center",
                color: "#1d7dd6",
                marginTop: "20px",
              }}
            >
              Forgot your Password?
            </p>
          </Link> */}
        </form>
        </div>

        {/* REMINDER DIALOG - shown after a successful sign-in attempt whose
            account or store subscription is within its reminder window
            (superAdmin-configured, see GlobalSettingsTab.js); the server
            is the only place that can evaluate this, since it requires
            looking up the specific account, so this can't appear before
            credentials are submitted. */}
            <Dialog
              open={showReminder}
              onClose={() => setShowReminder(false)}
              fullWidth
              maxWidth="xs"
              TransitionComponent={Grow}
            >
              <div className={styles.dialogHeader}>
                {reminderData?.source === "account" ? <EventBusyOutlinedIcon /> : <ScheduleOutlinedIcon />}
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  {reminderData?.source === "account" ? "Account Expiry Alert" : "Subscription Expiry Alert"}
                </Typography>
              </div>

            <DialogContent className={styles.dialogBody}>
              <div className={styles.reminderBadge}>
                <span className={styles.reminderBadgeDot} />
                <span className={styles.reminderBadgeCount}>{reminderData?.daysLeft}</span>
                <span className={styles.reminderBadgeLabel}>
                  day{reminderData?.daysLeft === 1 ? "" : "s"} left
                </span>
              </div>
              <Typography style={{ textAlign: "center" }}>
                {reminderData?.source === "account"
                  ? "Your account access is expiring soon."
                  : "This store's subscription is expiring soon."}
              </Typography>
              <Typography style={{ textAlign: "center" }}>
                Expiry Date:{" "}
                <strong>
                  {reminderData?.expiryDate &&
                    new Date(reminderData.expiryDate).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                </strong>
              </Typography>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button className={styles.cancelBtn} disableElevation onClick={() => setShowReminder(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setShowReminder(false);
                  // Get user type from localStorage to determine redirect
                  const storedUser = JSON.parse(localStorage.getItem("profile"));
                  const firstName = storedUser?.result?.name?.split(" ")[0];
                  if (storedUser?.result?.userType?.includes("superAdmin")) {
                    redirectWithWelcome("/Subscribers", firstName);
                  } else {
                    redirectWithWelcome("/PosMainScreen", firstName);
                  }
                }}
                className={styles.saveBtn}
                disableElevation
              >
                Confirm Login
              </Button>
            </DialogActions>
            </Dialog>

          {/* EXPIRED DIALOG - same timing constraint as the reminder above.
              Shared with SuperAdmin's Support Contact preview so the two can
              never drift apart; numbers come from the public branding
              endpoint, since a locked-out user has no session to read
              /global-settings with. */}
          <AccessExpiredDialog
            open={showExpired}
            onClose={() => setShowExpired(false)}
            message={expiredData?.message}
            businessName={publicBrand.businessName}
            callNumber={publicBrand.supportCallNumber}
            whatsappNumber={publicBrand.supportWhatsappNumber}
          />

      </div>
    </div>
  );
};

export default Login;
